import { useEffect, useState, useMemo } from "react";
import { MainLayout } from "./MainLayout";
import { useApp } from "./AppContext";
import { useTranslation } from "../i18n/TranslationContext";
import { useCurrentUser } from "../api/auth/useCurrentUser";
import { useGetExtraEvents } from "../api/extraEvents/useGetExtraEvents";
import {
	CheckCircle,
	Clock,
	MapPin,
	Users,
	Luggage,
	Info,
	Calendar,
} from "lucide-react";
import { formatDateDisplay, formatTime, parseEventDate } from "@/utils/dateUtils";
import {
	useTasksForExtra,
	useCompleteTask,
	useRevertTask,
	useTaskGroups,
	useCreateTasksForExtra,
} from "../hooks/useTasks";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { ExtraEventTask, SpecialTask } from "../api/tasks.api";
import { FeedbackWebView } from "./FeedbackWebView";
import { SpecialTaskBadge } from "./SpecialTaskBadge";
import { usePassengersSpecialTasks } from "../hooks/usePassengerSpecialTasks";
import { TaskInfoImageModal } from "./TaskInfoImageModal";
import eesKioskPage1 from "../assets/ees-kiosk-info-1.png";
import eesKioskPage2 from "../assets/ees-kiosk-info-2.png";
import eesKioskPage3 from "../assets/ees-kiosk-info-3.png";
import eesKioskPage4 from "../assets/ees-kiosk-info-4.png";

const eesKioskImages = [eesKioskPage1, eesKioskPage2, eesKioskPage3, eesKioskPage4];

const EES_KIOSK_TASK_IDS = [3, 4, 5, 6];

export function TaskList() {
	const { setCurrentScreen, navigateToMyEventWithContext, navigationContext } =
		useApp();
	const { t } = useTranslation();

	// Get current user and their events
	const { user } = useCurrentUser();
	const extraId = user?.extraId || 0;
	const { events } = useGetExtraEvents(
		extraId,
		user?.internal_extra ? "internal" : "external"
	);

	// Get the first booked event
	const upcomingEvent =
		events.find((event) => event.event_status === "booked") || events[0];
	const eventId = upcomingEvent?.event_id || 0;

	// Check if today is the event day
	const today = new Date();
	const eventDate = upcomingEvent
		? (parseEventDate(upcomingEvent.event_date) || new Date(upcomingEvent.event_date))
		: null;
	const isEventDay = eventDate
		? today.toDateString() === eventDate.toDateString()
		: false;
	const isBeforeEventDay = eventDate ? today < eventDate : false;

	// Task data hooks
	const {
		generalTasks,
		passengerTasks,
		allTasks,
		completedTasks,
		totalTasks,
		completionPercentage,
		isLoading,
		refetch: refetchTasks,
	} = useTaskGroups(extraId, eventId);

	const completeTaskMutation = useCompleteTask(extraId, eventId);
	const revertTaskMutation = useRevertTask(extraId, eventId);
	const createTasksMutation = useCreateTasksForExtra();

	// Get unique passenger IDs from tasks (memoized to prevent infinite re-renders)
	const passengerIds = useMemo(() => {
		return [
			...new Set(
				allTasks
					?.filter((task) => task.passenger_id)
					.map((task) => task.passenger_id as number) || []
			),
		];
	}, [allTasks]);

	// Fetch special tasks for all passengers
	const { specialTasksMap, isLoading: specialTasksLoading } =
		usePassengersSpecialTasks(passengerIds);

	// Feedback WebView state
	const [showFeedbackWebView, setShowFeedbackWebView] = useState(false);
	const [feedbackTask, setFeedbackTask] = useState<ExtraEventTask | null>(null);

	// PDF modal state
	const [showPdfModal, setShowPdfModal] = useState(false);

	// Handle task completion
	const handleCompleteTask = async (task: ExtraEventTask) => {
		if (task.status === "completed" || task.canComplete === false) {
			return;
		}

		// Handle feedback task specially
		if (task.task_type === "feedback") {
			setFeedbackTask(task);
			setShowFeedbackWebView(true);
			return;
		}

		try {
			await completeTaskMutation.mutateAsync(task.task_id);
			// Refresh task data to show updated state
			refetchTasks && (await refetchTasks());
		} catch (error) {
			// Error is handled by the mutation hook with German error messages
			console.error("Task completion error:", error);
		}
	};

	// Handle reverting the previous task back to pending
	const handleRevertPreviousTask = async (currentTask: ExtraEventTask) => {
		const currentIndex = allTasks.findIndex(
			(t) => t.task_id === currentTask.task_id
		);
		if (currentIndex <= 0) return;

		const previousTask = allTasks[currentIndex - 1];
		if (!previousTask || previousTask.status !== "completed") return;

		try {
			await revertTaskMutation.mutateAsync(previousTask.task_id);
			refetchTasks && (await refetchTasks());
		} catch (error) {
			console.error("Task revert error:", error);
		}
	};

	// Check if a task has EES Kiosk special tasks assigned
	const taskHasEesKiosk = (task: ExtraEventTask): boolean => {
		// Check across ALL assigned passengers (EES Kiosk is on template 7 which has no passenger_id)
		const allSpecialTasks = Object.values(specialTasksMap).flat();
		return allSpecialTasks.some(
			st => EES_KIOSK_TASK_IDS.includes(st.special_task_id) &&
				st.associated_task_template_id === task.task_template_id
		);
	};

	// Handle feedback completion (called from WebView when survey is completed)
	const handleFeedbackComplete = async () => {
		if (!feedbackTask) return;

		try {
			// Feedback task is automatically completed by the backend when feedback status is updated
			// We just need to refresh the task data and close the WebView
			refetchTasks && (await refetchTasks());

			// Close feedback WebView
			setShowFeedbackWebView(false);
			setFeedbackTask(null);
		} catch (error) {
			console.error("Error refreshing tasks after feedback completion:", error);
		}
	};

	// Handle feedback back/cancel
	const handleFeedbackBack = () => {
		setShowFeedbackWebView(false);
		setFeedbackTask(null);
	};

	// Show feedback WebView if requested
	if (showFeedbackWebView) {
		return (
			<FeedbackWebView
				onComplete={handleFeedbackComplete}
				onBack={handleFeedbackBack}
			/>
		);
	}

	// Get task icon based on type and content
	const getTaskIcon = (task: ExtraEventTask) => {
		if (
			task.task_name?.toLowerCase().includes("luggage") ||
			task.task_name?.toLowerCase().includes("gepäck")
		) {
			return <Luggage className="w-5 h-5" />;
		}
		if (
			task.task_name?.toLowerCase().includes("passenger") ||
			task.task_name?.toLowerCase().includes("check")
		) {
			return <Users className="w-5 h-5" />;
		}

		return <Info className="w-5 h-5" />;
	};

	// Get task status badge
	const getTaskStatusBadge = (task: ExtraEventTask) => {
		if (task.status === "completed") {
			return (
				<Badge variant="default" className="bg-green-100 text-green-800">
					Erledigt
				</Badge>
			);
		}

		return (
			<Badge variant="outline" className="bg-orange-100 text-orange-800">
				Ausstehend
			</Badge>
		);
	};

	// Check if task can be completed - trust backend canComplete field and check status
	const canCompleteTask = (task: ExtraEventTask) => {
		return task.canComplete === true && task.status === "pending";
	};

	// Format general task special requirements (for non-passenger tasks)
	const getGeneralTaskSpecialRequirements = (
		task: ExtraEventTask,
		specialTasksMap: Record<number, SpecialTask[]>
	) => {
		// Task 7 (Abflug Prozess) always shows airline info regardless of passenger assignment
		// For other tasks: Only show for tasks without specific passenger assignment (except task 5 and 6 which show airline)
		if (task.task_template_id !== 7 && task.passenger_first_name && task.task_template_id !== 5 && task.task_template_id !== 6) return null;

		// Get all special tasks for all passengers for this task template
		const allRelevantSpecialTasks: SpecialTask[] = [];
		Object.values(specialTasksMap).forEach((passengerSpecialTasks) => {
			const relevantTasks = passengerSpecialTasks.filter(
				(st) => st.associated_task_template_id === task.task_template_id
			);
			allRelevantSpecialTasks.push(...relevantTasks);
		});

		// Remove duplicates based on special_task_id
		const uniqueSpecialTasks = allRelevantSpecialTasks.filter(
			(task, index, array) =>
				array.findIndex((t) => t.special_task_id === task.special_task_id) ===
				index
		);

		// For task template 5, 6, and 7, get unique airlines from tasks of the same template
		const uniqueAirlines: string[] = [];
		if ((task.task_template_id === 5 || task.task_template_id === 6 || task.task_template_id === 7) && allTasks) {
			allTasks.forEach((t) => {
				if (
					t.task_template_id === task.task_template_id &&
					t.passenger_airline &&
					!uniqueAirlines.includes(t.passenger_airline)
				) {
					uniqueAirlines.push(t.passenger_airline);
				}
			});
		}

		// Task 7 always shows airline info, even if no special tasks
		if (task.task_template_id === 7 && uniqueAirlines.length > 0) {
			// Skip the null check for task 7 - always show airline info
		} else if (uniqueSpecialTasks.length === 0 && uniqueAirlines.length === 0) {
			return null;
		}

		return (
			<>
				{/* Show airlines for task template 5, 6, and 7 */}
				{uniqueAirlines.length > 0 && (
					<div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
						<div className="text-base text-blue-700">
							Sie übernehmen heute die Rolle eines Passagiers der Airline:{" "}
							<strong>{uniqueAirlines.join(", ")}</strong>
						</div>
					</div>
				)}
			</>
		);
	};

	// Format passenger info with special tasks
	const getPassengerInfo = (
		task: ExtraEventTask,
		specialTasksMap: Record<number, SpecialTask[]>
	) => {
		// Skip task 5, 6, and 7 - airline info is shown in getGeneralTaskSpecialRequirements
		if (task.task_template_id === 5 || task.task_template_id === 6 || task.task_template_id === 7) return null;

		if (!task.passenger_id) return null;

		const specialTasks = specialTasksMap[task.passenger_id || 0] || [];
		const relevantSpecialTasks = specialTasks.filter(
			(st) => st.associated_task_template_id === task.task_template_id
		);

		// No special tasks to show, return null
		if (relevantSpecialTasks.length === 0) return null;

		return (
			<div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
			</div>
		);
	};

	// Render initial task before event day
	const renderInitialTask = () => {
		if (!upcomingEvent || isEventDay) return null;

		return (
			<Card className="bg-white">
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Calendar className="w-5 h-5" />
						Anstehendes Event
					</CardTitle>
				</CardHeader>

				<CardContent>
					<div className="space-y-3">
						<div className="flex items-center gap-2 text-sm">
							<Calendar className="w-4 h-4" />
							<span>
								{formatDateDisplay(upcomingEvent.event_date)}
							</span>
						</div>
						<div className="flex items-center gap-2 text-sm">
							<Clock className="w-4 h-4" />
							<span>
								{formatTime(upcomingEvent.start_time)} -{" "}
								{formatTime(upcomingEvent.end_time)} Uhr
							</span>
						</div>
						<div className="p-3 bg-blue-50 rounded-lg">
							<p className="text-sm text-blue-900 text-left">
								Bitte seien Sie am{" "}
								{formatDateDisplay(upcomingEvent.event_date)},
								pünktlich vor Ort zum Probebetrieb. Die Schichtzeiten sind von{" "}
								{formatTime(upcomingEvent.start_time)} bis{" "}
								{formatTime(upcomingEvent.end_time)} Uhr. Bitte erscheinen Sie
								mindestens 15 Minuten vor Beginn vor Ort.
							</p>
						</div>
						{isEventDay && (
							<div className="flex items-center gap-2 text-green-600">
								<CheckCircle className="w-5 h-5" />
								<span className="font-medium">Heute ist Event-Tag!</span>
							</div>
						)}
					</div>
				</CardContent>
			</Card>
		);
	};

	// Render task card
	const renderTaskCard = (task: ExtraEventTask, index: number) => {
		const isCompleted = task.status === "completed";
		const canComplete = canCompleteTask(task);

		return (
			<Card
				key={task.task_id}
				className={`${isCompleted ? "bg-gray-50 opacity-75" : "bg-white"} transition-all duration-200`}
			>
				<CardHeader className="pb-3">
					<div className="flex items-center justify-between">
						<CardTitle className="flex items-center gap-3 text-lg">
							<span
								className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
									isCompleted
										? "bg-green-100 text-green-800"
										: "bg-gray-100 text-gray-600"
								}`}
							>
								{task.display_order}
							</span>
							<div className={isCompleted ? "line-through" : ""}>
								<div className="text-lg font-semibold">
									{task.task_name}
								</div>
								{task.subtitle && (
									<div className="text-sm font-normal text-gray-600 mt-1">
										{task.subtitle}
									</div>
								)}
							</div>
						</CardTitle>
						{getTaskStatusBadge(task)}
					</div>
					{task.location && (
						<div className="flex items-center gap-2 text-base text-gray-700 mt-1">
							<MapPin className="w-5 h-5" />
							<span className="font-bold">{task.location}</span>
						</div>
					)}
				</CardHeader>
				<CardContent>
					<div className="space-y-3">
						{getPassengerInfo(task, specialTasksMap)}
						{getGeneralTaskSpecialRequirements(task, specialTasksMap)}

						{isCompleted && task.completed_at && (
							<div className="flex items-center gap-2 text-sm text-green-600">
								<CheckCircle className="w-4 h-4" />
								<span>
									Abgeschlossen am{" "}
									{new Date(task.completed_at).toLocaleString("de-DE")}
								</span>
							</div>
						)}

						{!isCompleted &&
							(task.instructions || task.enhanced_instructions) && (
								<div className="space-y-3">
									{/* Regular Instructions */}
									{task.instructions && (
										<Alert>
											<AlertDescription>
												<div className="space-y-1">
													<div className="font-semibold text-sm text-gray-600 mb-2">
														Anweisungen:
													</div>
													<div className="text-base whitespace-pre-line" dangerouslySetInnerHTML={{ __html: task.instructions }} />
												</div>
											</AlertDescription>
										</Alert>
									)}

									{/* Enhanced Instructions (Special Tasks) */}
									{task.enhanced_instructions && (
										<>
											<Alert className="border-blue-200 bg-blue-50">
												<AlertDescription>
													<div className="space-y-1">
														<div className="font-semibold text-sm text-blue-800 mb-2">
															Besondere Anweisungen:
														</div>
														<div
															className="text-base text-blue-700 whitespace-pre-line [&_a]:underline [&_a]:font-semibold"
															dangerouslySetInnerHTML={{ __html: task.enhanced_instructions }}
														/>
													</div>
												</AlertDescription>
											</Alert>

											{/* More Info Button - Show for EES Kiosk special tasks */}
											{taskHasEesKiosk(task) && (
												<Button
													variant="outline"
													onClick={() => setShowPdfModal(true)}
													className="w-full py-2 text-base border-blue-300 text-blue-700 hover:bg-blue-50"
												>
													<Info className="w-4 h-4 mr-2" />
													Mehr Informationen
												</Button>
											)}
										</>
									)}
								</div>
							)}

						{!isCompleted && (
							<div className="space-y-2">
								{/* Back button - show regardless of canComplete status */}
								{![2, 4].includes(task.task_template_id) && (() => {
									const currentIndex = allTasks.findIndex(
										(t) => t.task_id === task.task_id
									);
									const previousTask = currentIndex > 0 ? allTasks[currentIndex - 1] : null;
									if (previousTask && previousTask.status === "completed") {
										return (
											<Button
												variant="outline"
												onClick={() => handleRevertPreviousTask(task)}
												disabled={revertTaskMutation.isPending}
												className="w-full py-4 text-base"
											>
												{revertTaskMutation.isPending
													? "Wird zurückgesetzt..."
													: "Zurück"}
											</Button>
										);
									}
									return null;
								})()}

								{/* Complete button - only show if task can be completed */}
								{canComplete && (
									<Button
										onClick={() => handleCompleteTask(task)}
										disabled={completeTaskMutation.isPending}
										className="w-full py-4 text-base bg-[var(--ucm-primary-teal)] hover:bg-[var(--ucm-primary-teal-hover)] text-white"
									>
										{completeTaskMutation.isPending
											? "Wird abgeschlossen..."
											: task.task_type === "feedback"
												? "Feedback-Formular öffnen"
												: "Aufgabe abschließen"}
									</Button>
								)}

								{/* Wait messages - show when task cannot be completed */}
								{!canComplete && (
									task.can_be_completed === false ? (
										<Alert className="border-orange-200 bg-orange-50">
											<Info className="h-4 w-4 text-orange-600" />
											<AlertDescription className="text-orange-800">
												Bitte warten Sie, bis der Supervisor diese Aufgabe freigeschaltet hat.
											</AlertDescription>
										</Alert>
									) : (
										<Alert>
											<Info className="h-4 w-4" />
											<AlertDescription>
												Diese Aufgabe kann erst abgeschlossen werden, wenn alle
												vorherigen Aufgaben erledigt sind.
											</AlertDescription>
										</Alert>
									)
								)}
							</div>
						)}
					</div>
				</CardContent>
			</Card>
		);
	};

	// Handle navigation back
	const handleBack = () => {
		if (navigationContext && navigationContext.sourceScreen === "my-event") {
			setCurrentScreen("my-event");
		} else if (
			navigationContext &&
			navigationContext.sourceScreen === "dashboard"
		) {
			navigateToMyEventWithContext();
		} else {
			setCurrentScreen("my-event");
		}
	};

	// Loading state
	if (isLoading) {
		return (
			<MainLayout showBack onBack={handleBack}>
				<div className="flex justify-center items-center h-64">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-transparent border-b-[var(--ucm-primary-teal)]"></div>
				</div>
			</MainLayout>
		);
	}

	return (
		<MainLayout showBack onBack={handleBack}>
			<div className="px-6">
				<div className="mb-6">
					<h1 className="text-2xl font-bold text-[var(--ucm-text-dark)] mb-2">
						{t("taskList.title")}
					</h1>
					{upcomingEvent && (
						<p className="text-[var(--ucm-text-medium)]">
							Probebetrieb am{" "}
							{formatDateDisplay(upcomingEvent.event_date)}
						</p>
					)}
				</div>

				{/* Progress Bar - Only show when tasks exist */}
				{totalTasks && totalTasks > 0 ? (
					<div className="mb-6 space-y-2">
						<div className="flex justify-between text-sm text-[var(--ucm-text-medium)]">
							<span>Fortschritt</span>
							<span>
								{completedTasks} von {totalTasks} Aufgaben
							</span>
						</div>
						<div className="w-full bg-gray-200 rounded-full h-2">
							<div
								className="bg-[var(--ucm-primary-teal)] h-2 rounded-full transition-all duration-300"
								style={{ width: `${completionPercentage}%` }}
							></div>
						</div>
					</div>
				) : null}

				<div className="space-y-4">
					{/* Initial Event Task - Always show before event day or when no tasks exist */}
					{(isBeforeEventDay ||
						(totalTasks !== undefined && totalTasks === 0)) &&
						renderInitialTask()}

					{/* Check-in Notice - Show on event day if not checked in and no tasks */}
					{isEventDay && totalTasks !== undefined && totalTasks === 0 ? (
						<Alert>
							<Info className="h-4 w-4" />
							<AlertDescription>
								Bitte führen Sie zuerst den Check-in durch, um Ihre Aufgaben zu
								sehen.
							</AlertDescription>
						</Alert>
					) : null}

					{/* Available Tasks - Show first pending task */}
					{totalTasks && totalTasks > 0 ? (
						<div className="space-y-4">
							{(() => {
								// Find the first pending task (regardless of canComplete status)
								const firstPendingTask = allTasks.find(
									(task) => task.status === "pending"
								);

								if (firstPendingTask) {
									return (
										<>
											<h2 className="text-lg font-semibold text-gray-900 pb-2">
												Verfügbare Aufgaben
											</h2>
											{renderTaskCard(firstPendingTask, 0)}
										</>
									);
								}

								// All tasks completed
								return (
									<div className="space-y-4">
										<Card className="bg-green-50 border-green-200">
											<CardContent className="p-6 text-left">
												<div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
													<span className="text-2xl">🎉</span>
												</div>
												<h3 className="text-lg font-semibold text-green-800 mb-2">
													Alle Aufgaben abgeschlossen!
												</h3>
												<p className="text-green-700 mb-4">
													Sie haben erfolgreich alle Aufgaben für den Probebetrieb
													abgeschlossen. Vielen Dank für Ihre Teilnahme!
												</p>
											</CardContent>
										</Card>
									</div>
								);
							})()}
						</div>
					) : null}
				</div>
			</div>

			{/* EES Kiosk Info PDF Modal */}
			<TaskInfoImageModal
				isOpen={showPdfModal}
				onClose={() => setShowPdfModal(false)}
				images={eesKioskImages}
				title="EES Kiosk Information"
			/>
		</MainLayout>
	);
}
